import React, { useState, useEffect } from 'react';
import { crmAPI } from '../../lib/api';
import { 
    Plus, Search, RefreshCw, FileText, CheckCircle, XCircle, 
    Download, Eye, ChevronDown, ChevronUp, Calendar, Building2
} from 'lucide-react';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const GenerateInvoice = () => {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [lifts, setLifts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingLifts, setFetchingLifts] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [expandedLift, setExpandedLift] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [createdInvoices, setCreatedInvoices] = useState([]);
    
    // Work order details (optional)
    const [workOrderDetails, setWorkOrderDetails] = useState({
        work_order_no: '',
        work_order_date: '',
        noa_ref: '',
        noa_date: '',
        coa_ref: '',
        coa_date: '',
        invoice_date: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await crmAPI.getProjects();
            setProjects(response.data.data || response.data);
        } catch (err) {
            console.error('Failed to load projects:', err);
            setError('Failed to load projects');
        } finally {
            setLoading(false);
        }
    };

    const fetchLiftsForBilling = async () => {
        if (!selectedProject || selectedMonths.length === 0) {
            setError('Please select a project and at least one month');
            return;
        }

        try {
            setFetchingLifts(true);
            setError('');
            const response = await crmAPI.getLiftsForBilling({
                project_id: selectedProject,
                months: selectedMonths,
                year: selectedYear
            });
            
            if (response.data.success) {
                setLifts(response.data.data);
            } else {
                setError(response.data.message || 'Failed to load lifts');
            }
        } catch (err) {
            console.error('Failed to load lifts:', err);
            setError('Failed to load lifts for billing');
        } finally {
            setFetchingLifts(false);
        }
    };

    const handleMonthToggle = (month) => {
        setSelectedMonths(prev => {
            if (prev.includes(month)) {
                return prev.filter(m => m !== month);
            } else {
                return [...prev, month];
            }
        });
    };

    const handleSelectAllMonths = () => {
        setSelectedMonths([...MONTHS]);
    };

    const handleClearMonths = () => {
        setSelectedMonths([]);
    };

    const handleGenerateInvoice = async () => {
        if (!selectedProject || selectedMonths.length === 0) {
            setError('Please select a project and at least one month');
            return;
        }

        // Check if any lifts are selected (or select all)
        const uninvoicedLifts = lifts.filter(lift => 
            lift.billing_status.some(status => !status.invoiced)
        );

        if (uninvoicedLifts.length === 0) {
            setError('All selected months have already been invoiced for all lifts');
            return;
        }

        try {
            setGenerating(true);
            setError('');
            
            const monthNums = selectedMonths.map(m => MONTHS.indexOf(m) + 1);
            
            const response = await crmAPI.generateMultiMonthInvoice({
                project_id: selectedProject,
                invoice_date: workOrderDetails.invoice_date,
                billing_year: selectedYear,
                billing_months: monthNums,
                billing_month_names: selectedMonths,
                work_order_no: workOrderDetails.work_order_no,
                work_order_date: workOrderDetails.work_order_date || null,
                noa_ref: workOrderDetails.noa_ref,
                noa_date: workOrderDetails.noa_date || null,
                coa_ref: workOrderDetails.coa_ref,
                coa_date: workOrderDetails.coa_date || null,
            });

            if (response.data.success) {
                setSuccess(`Successfully generated ${response.data.count} invoice(s)`);
                setCreatedInvoices(response.data.data);
                // Refresh lifts to show updated status
                fetchLiftsForBilling();
            } else {
                setError(response.data.message || 'Failed to generate invoices');
            }
        } catch (err) {
            console.error('Failed to generate invoices:', err);
            setError(err.response?.data?.message || 'Failed to generate invoices');
        } finally {
            setGenerating(false);
        }
    };

    const toggleLiftExpand = (liftId) => {
        setExpandedLift(prev => prev === liftId ? null : liftId);
    };

    const getTotalForMonth = (month) => {
        return lifts.reduce((sum, lift) => {
            const status = lift.billing_status.find(s => s.month === month);
            if (status && !status.invoiced) {
                return sum + (lift.total_amount || 0);
            }
            return sum;
        }, 0);
    };

    const getGrandTotal = () => {
        return selectedMonths.reduce((sum, month) => sum + getTotalForMonth(month), 0);
    };

    const getInvoicedCount = () => {
        return lifts.reduce((count, lift) => {
            const invoicedMonths = lift.billing_status.filter(s => s.invoiced).length;
            return count + invoicedMonths;
        }, 0);
    };

    const getPendingCount = () => {
        return lifts.reduce((count, lift) => {
            const pendingMonths = lift.billing_status.filter(s => !s.invoiced).length;
            return count + pendingMonths;
        }, 0);
    };

    const selectedProjectData = projects.find(p => p.id === parseInt(selectedProject));

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                    Generate Invoice from Lifts
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Select month(s) to view lift maintenance records and generate invoices
                </p>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center justify-between">
                    <span>{error}</span>
                    <button onClick={() => setError('')} className="text-red-700 hover:text-red-900">
                        <XCircle className="w-5 h-5" />
                    </button>
                </div>
            )}

            {success && (
                <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center justify-between">
                    <span>{success}</span>
                    <button onClick={() => setSuccess('')} className="text-green-700 hover:text-green-900">
                        <XCircle className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Filters Section */}
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Project Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Select Project <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedProject}
                            onChange={(e) => {
                                setSelectedProject(e.target.value);
                                setLifts([]);
                                setSelectedMonths([]);
                            }}
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700"
                        >
                            <option value="">-- Select Project --</option>
                            {projects.map(project => (
                                <option key={project.id} value={project.id}>
                                    {project.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Year Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Year <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedYear}
                            onChange={(e) => {
                                setSelectedYear(parseInt(e.target.value));
                                setLifts([]);
                            }}
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700"
                        >
                            {[2024, 2025, 2026, 2027, 2028].map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    {/* Invoice Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Invoice Date
                        </label>
                        <input
                            type="date"
                            value={workOrderDetails.invoice_date}
                            onChange={(e) => setWorkOrderDetails({...workOrderDetails, invoice_date: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700"
                        />
                    </div>

                    {/* Action Button */}
                    <div className="flex items-end">
                        <button
                            onClick={fetchLiftsForBilling}
                            disabled={!selectedProject || selectedMonths.length === 0 || fetchingLifts}
                            className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {fetchingLifts ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                                <Search className="w-4 h-4" />
                            )}
                            Load Lifts
                        </button>
                    </div>
                </div>

                {/* Month Selection */}
                <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Select Month(s) <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={handleSelectAllMonths}
                                className="text-xs text-indigo-600 hover:text-indigo-800"
                            >
                                Select All
                            </button>
                            <span className="text-gray-400">|</span>
                            <button
                                onClick={handleClearMonths}
                                className="text-xs text-gray-600 hover:text-gray-800"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {MONTHS.map(month => (
                            <button
                                key={month}
                                onClick={() => handleMonthToggle(month)}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                    selectedMonths.includes(month)
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-300'
                                }`}
                            >
                                {month}
                            </button>
                        ))}
                    </div>
                    {selectedMonths.length > 0 && (
                        <p className="text-sm text-gray-500 mt-2">
                            {selectedMonths.length} month(s) selected: {selectedMonths.join(', ')}
                        </p>
                    )}
                </div>
            </div>

            {/* Work Order Details (Optional) */}
            {selectedProject && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">
                        Work Order Details (Optional)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Work Order No.
                            </label>
                            <input
                                type="text"
                                value={workOrderDetails.work_order_no}
                                onChange={(e) => setWorkOrderDetails({...workOrderDetails, work_order_no: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700"
                                placeholder="e.g., 322500328"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Work Order Date
                            </label>
                            <input
                                type="date"
                                value={workOrderDetails.work_order_date}
                                onChange={(e) => setWorkOrderDetails({...workOrderDetails, work_order_date: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                NOA Ref
                            </label>
                            <input
                                type="text"
                                value={workOrderDetails.noa_ref}
                                onChange={(e) => setWorkOrderDetails({...workOrderDetails, noa_ref: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                NOA Date
                            </label>
                            <input
                                type="date"
                                value={workOrderDetails.noa_date}
                                onChange={(e) => setWorkOrderDetails({...workOrderDetails, noa_date: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                COA Ref
                            </label>
                            <input
                                type="text"
                                value={workOrderDetails.coa_ref}
                                onChange={(e) => setWorkOrderDetails({...workOrderDetails, coa_ref: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                COA Date
                            </label>
                            <input
                                type="date"
                                value={workOrderDetails.coa_date}
                                onChange={(e) => setWorkOrderDetails({...workOrderDetails, coa_date: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Lifts Table */}
            {lifts.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
                    <div className="p-4 border-b border-gray-200 dark:border-slate-600 flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
                                Lift Maintenance Records
                            </h3>
                            <p className="text-sm text-gray-500">
                                {selectedProjectData?.name} - {selectedYear}
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="text-sm">
                                <span className="text-green-600 font-medium">{getInvoicedCount()}</span> invoiced
                                <span className="mx-2">|</span>
                                <span className="text-orange-600 font-medium">{getPendingCount()}</span> pending
                            </div>
                            <button
                                onClick={handleGenerateInvoice}
                                disabled={generating || getPendingCount() === 0}
                                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                            >
                                {generating ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                    <FileText className="w-4 h-4" />
                                )}
                                Generate Invoice{selectedMonths.length > 1 ? 's' : ''}
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-700">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase w-8"></th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Lift Details</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Rate</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Total</th>
                                    {selectedMonths.map(month => (
                                        <th key={month} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            <div className="flex flex-col">
                                                <span>{month.substring(0, 3)}</span>
                                                <span className="text-green-600 font-normal">
                                                    {getTotalForMonth(month) > 0 ? `৳${getTotalForMonth(month).toLocaleString()}` : '-'}
                                                </span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {lifts.map(lift => (
                                    <React.Fragment key={lift.id}>
                                        <tr className={`hover:bg-gray-50 dark:hover:bg-slate-700 ${lift.all_months_invoiced ? 'bg-green-50 dark:bg-green-900/20' : ''}`}>
                                            <td className="px-4 py-3">
                                                <button
                                                    onClick={() => toggleLiftExpand(lift.id)}
                                                    className="p-1 hover:bg-gray-200 rounded"
                                                >
                                                    {expandedLift === lift.id ? (
                                                        <ChevronUp className="w-4 h-4 text-gray-500" />
                                                    ) : (
                                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <div>
                                                        <p className="font-medium text-gray-800 dark:text-gray-200">
                                                            {lift.name}
                                                        </p>
                                                        <p className="text-sm text-gray-500">
                                                            {lift.brand} | {lift.capacity}kg | {lift.stop} stops
                                                        </p>
                                                        <p className="text-xs text-gray-400">
                                                            {lift.location}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                                ৳{lift.monthly_rate?.toLocaleString() || 0}
                                            </td>
                                            <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">
                                                ৳{lift.total_amount?.toLocaleString() || 0}
                                            </td>
                                            {selectedMonths.map(month => {
                                                const status = lift.billing_status?.find(s => s.month === month);
                                                return (
                                                    <td key={month} className="px-4 py-3">
                                                        {status?.invoiced ? (
                                                            <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                                                <CheckCircle className="w-4 h-4" />
                                                                Invoiced
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 text-orange-600 text-sm">
                                                                <Calendar className="w-4 h-4" />
                                                                Pending
                                                            </span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                        {expandedLift === lift.id && (
                                            <tr>
                                                <td colSpan={4 + selectedMonths.length} className="px-4 py-3 bg-gray-50 dark:bg-slate-700">
                                                    <div className="text-sm">
                                                        <p><strong>Item Code:</strong> {lift.item_code || 'N/A'}</p>
                                                        <p><strong>Quantity:</strong> {lift.quantity || 1}</p>
                                                        <p><strong>Monthly Rate:</strong> ৳{lift.monthly_rate?.toLocaleString() || 0}</p>
                                                        <p><strong>Total per Month:</strong> ৳{lift.total_amount?.toLocaleString() || 0}</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-100 dark:bg-slate-600 font-medium">
                                <tr>
                                    <td colSpan={3} className="px-4 py-3 text-right">
                                        Total for Selected Months:
                                    </td>
                                    <td className="px-4 py-3">
                                        ৳{getGrandTotal().toLocaleString()}
                                    </td>
                                    <td colSpan={selectedMonths.length}></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            {/* No Lifts Message */}
            {!loading && !fetchingLifts && selectedProject && selectedMonths.length > 0 && lifts.length === 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-8 text-center">
                    <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                        No lifts found for this project. Please add lifts first.
                    </p>
                </div>
            )}
        </div>
    );
};

export default GenerateInvoice;
